import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useInternals, useShowInternals } from '../contexts/InternalsContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useTheme } from '../contexts/ThemeContext';
import BetaBadge from './BetaBadge';
import Logo from './Logo';
import LegalLinks from './LegalLinks';
import PageErrorBoundary from './PageErrorBoundary';
import { rememberPlace, recallPlace } from './navMemory';
import { useEntitlements } from '../contexts/EntitlementContext';
import { shellFromHost, SHELLS } from '../shell/shell';
import { crossShellHref } from '../shell/routeShell';
import { LAST_SHELL_KEY } from '../shell/houseLogin';
import { browserStore, hasResume, saveSpot, spotFor, withoutResume } from '../shell/lastSpot';
import { writePref } from '../services/crossShellPrefs';
import { visibleSections } from './navVisibility';
import { returnLabelFor } from './common/returnPath';
import AccountDrawer from './AccountDrawer';
import type { Product } from '../types';

/** Sun/moon toggle for the app theme. Shows the icon of the theme you'd switch TO. */
function ThemeToggle({ className = '' }: { className?: string }) {
  const { theme, toggleTheme } = useTheme();
  const dark = theme === 'dark';
  return (
    <button
      onClick={toggleTheme}
      aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
      title={dark ? 'Switch to light theme' : 'Switch to dark theme'}
      className={`inline-flex items-center justify-center rounded-md p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 ${className}`}
    >
      {dark ? (
        // sun — click to go light
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ) : (
        // moon — click to go dark
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      )}
    </button>
  );
}

interface NavItem {
  path: string;
  label: string;
  sticky?: boolean;  // Whether to remember drill-down state
  // Extra route prefixes that belong to this nav item's scope but live
  // under a different path. The page editor (/page-builder/:id) is the
  // deepest level of the Projects drill-down (project → category → topic
  // → keyword → page), so it counts as "inside Projects": being there
  // saves under the Projects sticky slot, the Projects tab highlights, and
  // clicking Projects from elsewhere restores you right back into the
  // editor. No new tab needed.
  extraPaths?: string[];
  /** The product that owns this entry, where it differs from its section's.
   *  Artifacts is the one that needs it: the shelf spans every product, but
   *  Pages belongs to Publish. */
  product?: Product;
}

interface Section {
  key: string;
  label: string;
  items: NavItem[];
  /** The product that owns this stage. Absent = the shared spine, visible in
   *  every shell (Home, the artifacts shelf). See `navVisibility.ts`. */
  product?: Product;
  /** Product shells a shared-spine section stays out of. */
  notIn?: Product[];
}

/**
 * THE NAV IS THE WORKFLOW NOW, not a list of engines.
 *
 * It used to be six machines side by side — Dashboard, Projects, Pages,
 * Brainstorms, News Beats, Trending — and not one of them named something a
 * user wants to end up with. People arrive wanting "a weekly briefing on
 * offshore wind"; the app answered with a choice of subsystems, and the only
 * way to learn which subsystem to open was to have built the thing already.
 *
 * Each entry is a noun a person recognises rather than a subsystem: Home,
 * Subjects, Trends, News Beats, Artifacts, Deliver. The second row is what that
 * entry contains; one with a single entry shows no second row and links
 * straight through.
 *
 * Subjects, Trends and Deliver are STAGES; Artifacts is the SHELF between them.
 * Links used to be a top-level entry of its own, on the argument that a link
 * map spans every stage. It moved once a page started carrying its own publish
 * path: a link map is about where things are published, which is delivery.
 *
 * URLS ARE UNCHANGED, deliberately. Every screen keeps the route it had, so
 * bookmarks, the sticky-nav memory, and `/page-builder` counting as "inside
 * Projects" all keep working — this is a change to how the app EXPLAINS itself,
 * and it would be a poor trade to break every saved link to make it.
 *
 * THIS IS THE FULL LIST, AND IT IS NOT WHAT RENDERS. Each section names the
 * product that owns it, and `visibleSections` narrows the list to the current
 * shell (`beats.lumnian.com` shows News Beats and the shelf, nothing else) and
 * then to what the user has bought. The full list stays authoritative for
 * `sectionFor` and `activeItemIn`, so a deep link into a route this shell does
 * not show still resolves to a coherent header instead of an unhighlighted row.
 */
const SECTIONS: Section[] = [
  // NOT IN RECON: its Home was the room list, a second address for Recons.
  { key: 'home', label: 'Home', notIn: ['BOARDROOM'], items: [{ path: '/', label: 'Home' }] },
  {
    // SUBJECTS IS ITS OWN ENTRY. It used to share a "Research" stage with the
    // two trend tables, which put Subjects behind a second row and named the
    // stage after a word Recon also uses. Trends moved to its own section
    // below, so this one holds a single entry and needs no second row.
    key: 'subjects',
    label: 'Subjects',
    product: 'PUBLISH',
    items: [
      {
        // A page carries its own publish path. The subject tree suggests that
        // path once, at creation, and stops owning it. What is left in the
        // workspace is what you know about a subject and what you want to cover.
        path: '/subjects',
        label: 'Subjects',
        sticky: true,
        // `/projects` redirects here, so it must HIGHLIGHT or the row blanks
        // while you are plainly still inside a subject.
        //
        // `/page-builder` USED to be here too, on the reasoning that a page is
        // a node's page. It moved to Artifacts, for the same reason as above.
        extraPaths: ['/projects'],
      },
    ],
  },
  {
    // TRENDS IS FINDING WORK, NOT DOING IT: what people search for now, and the
    // keywords those searches turned up. It comes before Subjects in practice
    // ("Start a subject" and "Add to subject" live on these tables), so it is
    // its own section rather than a tab under Subjects. Clicking it lands on
    // "Trending now", which starts a search — that is what you clicked for.
    key: 'trends',
    label: 'Trends',
    product: 'PUBLISH',
    items: [
      // THE TWO TABLES, PROMOTED OUT OF A PAGE. They were tabs inside "Trends",
      // which made a third layer of navigation whose middle rung named nothing
      // you could look at. `activeItemIn` takes the longest match, so
      // `/trending/keywords` highlights its own entry rather than both.
      { path: '/trending', label: 'Trending now' },
      // "Recent", not "Your research": a trending run pulls keywords on your
      // behalf, so the table holds rows nobody deliberately asked for. See
      // TRENDS_TABS in pages/trendFreshness.ts.
      { path: '/trending/keywords', label: 'Recent' },
    ],
  },
  {
    // Its own product, served at boardroom.lumnian.com. Flat — one list, one
    // room — and NOT sticky for that reason: there is nothing under the room
    // worth restoring a position in, and remembering `/boardroom/12` would only
    // fire when you arrive from OUTSIDE the section (clicking Boardroom from
    // Settings would reopen the last room instead of the list). A room is
    // somewhere you go on purpose, by name, off the list.
    //
    // TWO ENTRIES IN THE TOP BAR, NOT ONE WITH A SECOND ROW. "Recon" opened a
    // row holding Recons and Watch, a whole row of height on every page for
    // two links. A section of one entry has no second row.
    key: 'boardroom',
    label: 'Recons',
    product: 'BOARDROOM',
    items: [{ path: '/boardroom', label: 'Recons' }],
  },
  {
    key: 'watch',
    label: 'Watch',
    product: 'BOARDROOM',
    // Sticky: a watch subject IS somewhere you come back to — unlike a room, which you open
    // by name off the list. Coming back to the subject you were reading is the right default.
    items: [{ path: '/watch', label: 'Watch', sticky: true }],
  },
  {
    // The one producer that is not inside a subject. Everything else you make
    // is made from a node; a beat stands on its own and runs on a cadence.
    key: 'news-beats',
    label: 'News Beats',
    product: 'BEATS',
    items: [{ path: '/news-beats', label: 'News Beats', sticky: true }],
  },
  {
    // The SHELF: what you have, across every kind. Pages sits inside it rather
    // than beside it because a page IS an artifact — /artifacts is the strict
    // superset, and /pages is the bulk workbench over the one kind that has
    // bulk verbs. Side by side they read as two unrelated screens and you had
    // to learn which was which.
    key: 'artifacts',
    label: 'Artifacts',
    // NOT IN RECON. There it was mostly site-builder pages under seven
    // filters; what Recon makes for it is share links, and the room's Share
    // button lists those.
    notIn: ['BOARDROOM'],
    items: [
      { path: '/artifacts', label: 'Everything' },
      {
        path: '/pages',
        label: 'Pages',
        sticky: true,
        // The SHELF spans every product; a page does not. A Beats user sees
        // Everything and not Pages.
        product: 'PUBLISH',
        // Editing a page happens here now, not under Subjects. The route is
        // unchanged — this file moves what a screen BELONGS to, never its URL.
        extraPaths: ['/page-builder'],
      },
    ],
  },
  {
    key: 'deliver',
    label: 'Deliver',
    product: 'PUBLISH',
    // Publishing was three clicks inside Settings, which framed shipping as
    // configuration — you set a deployment target the way you set an API key.
    // It is a stage of the work.
    items: [
      { path: '/deliver', label: 'Sites & deploys', sticky: true },
      {
        // READING WHAT IS ABOUT TO GO LIVE. A stage of delivering rather than a
        // tab of one site: the queue spans every site that asks for review, and
        // the point of it is that somebody works the whole thing down to zero.
        //
        // NOT sticky. There is no drill-down to return to — the queue is
        // wherever it is now — and restoring a position in a list that has
        // since changed would put you on a page you already decided.
        path: '/deliver/review',
        label: 'Review',
      },
      {
        // LINKS IS PART OF DELIVERING. It was its own entry, lifted out of the
        // subject workspace because a link map is drawn ACROSS subjects and
        // across sites and a tab inside one subject could express neither.
        // That argument stands — it just points here: a cross-site map is a
        // property of where things are PUBLISHED, and `link_href` already
        // takes a from-site and a to-site because a link only resolves in
        // terms of the sites at both ends.
        path: '/link-builder',
        label: 'Link Builder',
      },
    ],
  },
];

const navItems: NavItem[] = SECTIONS.flatMap((s) => s.items);

// True when `pathname` falls within a nav item's scope (its own path or any
// of its extraPaths). Root ('/') matches only exactly so it doesn't swallow
// every route.
function navMatches(item: NavItem, pathname: string): boolean {
  if (item.path === '/') return pathname === '/';
  if (pathname.startsWith(item.path)) return true;
  return (item.extraPaths ?? []).some((p) => pathname.startsWith(p));
}

/**
 * The ONE entry in a stage that a route belongs to.
 *
 * `navMatches` is a prefix test, so a nested entry matches its parent too:
 * `/deliver/review` is inside `/deliver`, and both would light up. Two
 * highlighted entries is the same bug as none — the row stops telling you where
 * you are. Longest match wins, which is the only reading that can be right when
 * one entry lives underneath another.
 */
function activeItemIn(section: Section, pathname: string): NavItem | null {
  const matches = section.items.filter((i) => navMatches(i, pathname));
  if (!matches.length) return null;
  return matches.reduce((best, i) => (i.path.length > best.path.length ? i : best));
}

/** The stage a route belongs to, or null for routes that sit outside the
 *  workflow entirely (Settings, your account). */
function sectionFor(pathname: string): Section | null {
  return (
    SECTIONS.find((sec) => sec.items.some((i) => navMatches(i, pathname))) ?? null
  );
}

export default function Layout() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const showInternals = useShowInternals();
  const { setRevealed } = useInternals();
  const { held, loading: entitlementsLoading, catalogOf } = useEntitlements();
  // The hostname decides which product app this is. `app.lumnian.com` is the
  // house; anything unrecognised (localhost, an IP) falls back to it.
  const shell = shellFromHost(window.location.hostname);

  /**
   * Remember which product this browser works in, so the house login can send
   * the next sign-in straight back here instead of to the launcher.
   *
   * A shared cookie, not localStorage: the login page lives at `app.<domain>`
   * and localStorage written here would be invisible there. Only a shell whose
   * product is HELD is remembered — a shell you got 402'd in is not "yours",
   * and the house is the right place to wake up in.
   */
  const shellIsHeld =
    !entitlementsLoading &&
    SHELLS[shell].product !== null &&
    held.includes(SHELLS[shell].product as Product);
  useEffect(() => {
    if (shellIsHeld) writePref(LAST_SHELL_KEY, shell);
  }, [shell, shellIsHeld]);
  const shellCatalog =
    SHELLS[shell].product !== null ? catalogOf(SHELLS[shell].product as Product) : null;
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  // THE LAST SPOT. Every page you reach is remembered for you, on this site;
  // arriving fresh from the login page (`?resume=1`) goes back to it, or just
  // drops the mark when there is nothing of yours to go back to. See
  // `shell/lastSpot.ts`.
  useEffect(() => {
    if (!user) return;
    if (hasResume(location.search)) {
      const spot = spotFor(browserStore(), user.id);
      navigate(spot ?? `${location.pathname}${withoutResume(location.search)}${location.hash}`, { replace: true });
      return;
    }
    saveSpot(browserStore(), user.id, `${location.pathname}${location.search}${location.hash}`);
  }, [user, location.pathname, location.search, location.hash, navigate]);
  /**
   * A URL that belongs to another product goes to that product's host.
   *
   * The links themselves are ShellLinks now, but a link is only one way to
   * arrive: an old bookmark, a pasted URL, a legacy redirect in the route table
   * or a link this codebase has not been taught about yet all land here instead.
   * Without this the route simply RENDERS — Publish inside the Brainstorms
   * shell, with the wrong nav around it — which is the bug that started this,
   * and it looks like a broken app rather than a wrong address.
   *
   * `replace`, not assign: the wrong-host URL should not sit in history waiting
   * for a Back press to bounce you off the app again. No loop is possible —
   * `crossShellHref` returns null once you are on the right host.
   */
  useEffect(() => {
    const href = crossShellHref(
      `${location.pathname}${location.search}`,
      window.location.hostname,
      window.location.origin,
    );
    if (href) window.location.replace(href);
  }, [location.pathname, location.search]);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [networkDisabledBanner, setNetworkDisabledBanner] = useState<string | null>(null);
  const [googleAdsExpiredBanner, setGoogleAdsExpiredBanner] = useState(false);
  const [googleAdsNotConfiguredBanner, setGoogleAdsNotConfiguredBanner] = useState<string | null>(null);
  const [googleAdsDemandDownBanner, setGoogleAdsDemandDownBanner] = useState<string | null>(null);
  const { subscribe } = useWebSocket();

  useEffect(() => {
    return subscribe((event) => {
      if (event.event_type === 'network_disabled') {
        setNetworkDisabledBanner(event.message);
      }
      if (event.event_type === 'google_ads_auth_expired') {
        setGoogleAdsExpiredBanner(true);
      }
      if (event.event_type === 'google_ads_not_configured') {
        setGoogleAdsNotConfiguredBanner(event.message);
      }
      if (event.event_type === 'google_ads_demand_down') {
        setGoogleAdsDemandDownBanner(event.message);
      }
    });
  }, [subscribe]);

  // Save current URL state for sticky pages.
  //
  // BY THE ITEM'S OWN PATH, NEVER ITS extraPaths. An extraPath means "highlight
  // this entry while you are here", which is not the same as "this is a place
  // within this entry worth returning to". `/page-builder/:id` is an extraPath
  // of Pages: saving it there made clicking ARTIFACTS reopen the last editor
  // instead of the shelf, and the editor's own trail led back to Subjects — so
  // there was no way from Artifacts to a list of artifacts at all.
  //
  // Drill-downs that ARE worth restoring all live under their entry's own path
  // (`/subjects/1`, `/boardroom/12`), so they still save exactly as before.
  //
  // OPT-IN. `rememberPlace` is a no-op unless the user has turned "pick up where I left
  // off" on in their account, so by default nothing here is written down at all.
  useEffect(() => {
    const currentPath = location.pathname;
    const stickyItem = navItems.find(
      (item) => item.sticky && currentPath.startsWith(item.path)
    );
    if (stickyItem) {
      rememberPlace(stickyItem.path, currentPath + location.search);
    }
  }, [location.pathname, location.search]);

  // Clicking a STAGE goes to its first entry — or, with the resume preference on and a
  // place remembered in that stage, back to wherever you left off in it. `recallPlace`
  // returns null when the preference is off, which is what makes the plain behaviour
  // (always the stage's first tab) the default.
  const handleSectionClick = (section: Section, e: React.MouseEvent) => {
    e.preventDefault();
    const current = sectionFor(location.pathname);
    if (current?.key === section.key) {
      // ALREADY IN THIS STAGE — which is not the same as being at the top of it.
      //
      // This used to `return` unconditionally, to stop clicking the active stage
      // from throwing away whichever second-row tab you were on. That intent is
      // right and is kept: standing exactly on one of the stage's entries, the
      // click still does nothing.
      //
      // But a DRILL-DOWN is not one of those entries, and for a stage with a
      // single entry there is no second row at all — so the stage button was the
      // only way out of a room, and it did nothing. You could open a room and
      // have no nav affordance that returned you to the list.
      //
      // Up one level, then: to the entry you are inside. `/boardroom/12` →
      // `/boardroom`, `/subjects/7` → `/subjects`, while `/trending/keywords`
      // is itself an entry and so still holds still.
      const here = activeItemIn(section, location.pathname);
      if (here && location.pathname !== here.path) navigate(here.path);
      return;
    }
    for (const item of section.items) {
      if (!item.sticky) continue;
      const saved = recallPlace(item.path);
      if (saved) {
        navigate(saved);
        return;
      }
    }
    navigate(section.items[0].path);
  };

  const handleNavClick = (item: NavItem, e: React.MouseEvent) => {
    if (item.sticky) {
      e.preventDefault();

      // If already within this scope (including the page editor, which counts
      // as inside Pages), clicking the tab resets to its base list.
      if (navMatches(item, location.pathname)) {
        navigate(item.path);
        return;
      }

      // Coming from outside the scope — restore the saved drill-down, else go
      // to the base path. Null whenever the resume preference is off, so the
      // default is always the base list.
      navigate(recallPlace(item.path) || item.path);
    }
  };

  // The full-height editors (a boardroom, the page-builder workspace) own their
  // own layout: they skip the page card and the footer, both of which would add
  // scroll below a workspace that's already sized to the viewport.
  const isFullHeightEditor =
    /^\/(boardroom|page-builder|subjects|link-builder)\/[^/]+/.test(
      location.pathname,
    ) ||
    // The review queue is a pager: the body being read wants the viewport, and
    // a card with its own scroll inside the page's scroll gives it two.
    location.pathname === '/deliver/review';

  // Which stage the current route belongs to. Null on Settings and /account —
  // they sit outside the workflow, so no stage should look active while you're
  // in them.
  const activeSection = sectionFor(location.pathname);

  // WHAT ACTUALLY RENDERS. Derived once and used by all three nav surfaces —
  // the desktop row, the second row and the mobile menu. The mobile menu used
  // to map SECTIONS a second time, which is exactly how two navs drift into
  // disagreeing about what the app contains.
  const shellProduct = SHELLS[shell].product;
  const shownSections = visibleSections(SECTIONS, shellProduct, held);

  // The second row shows the ACTIVE section's items, narrowed the same way —
  // otherwise the beats shell would offer Pages under Artifacts.
  const activeShown = activeSection
    ? shownSections.find((sec) => sec.key === activeSection.key) ?? null
    : null;
  // A stage with one entry needs no second row: the stage name already said it.
  const subNav = activeShown && activeShown.items.length > 1 ? activeShown : null;

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-950">
      <nav className="bg-white shadow-sm dark:bg-gray-800 dark:shadow-none dark:border-b dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-14 sm:h-16">
            <div className="flex">
              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="sm:hidden inline-flex items-center justify-center p-2 -ml-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700"
              >
                <span className="sr-only">Open main menu</span>
                {mobileMenuOpen ? (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
              <div className="flex-shrink-0 flex items-center gap-2 ml-2 sm:ml-0">
                <Logo size={26} />
                {/* THE WORDMARK IS THE SHELL, not the company. Someone in
                    beats.lumnian.com is using News Beats and should be told so
                    — a shared "Lumnian" across four different apps is how the
                    products stop feeling like products. In the house it is the
                    company name, because that is what the house is. */}
                <Link
                  to={SHELLS[shell].home}
                  className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100"
                >
                  {SHELLS[shell].name}
                </Link>
                <BetaBadge />
              </div>
              {/* Desktop nav — the three stages, plus Home */}
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                {shownSections.map((section) => (
                  <Link
                    key={section.key}
                    to={section.items[0].path}
                    onClick={(e) => handleSectionClick(section, e)}
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                      activeSection?.key === section.key
                        ? 'border-blue-500 text-gray-900 dark:text-gray-100'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-600'
                    }`}
                  >
                    {section.label}
                  </Link>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <ThemeToggle />
              {/* NO SETTINGS LINK HERE. It lived in the bar AND in the
                  account drawer, which is where every other account-shaped
                  destination already is — Artifacts, Account settings, billing.
                  The drawer's is also the better one: it stamps a return trail
                  through `withReturnTo`, so Settings has a way back, while this
                  one dropped you there with none. Mobile keeps its own copy in
                  the hamburger menu, which has no drawer to reach. The divider
                  that used to sit here went with it — it separated Settings
                  from your name, and between an icon toggle and a name it is
                  just a mark. */}
              {/* Your name opens a DRAWER, not a page.
                  It used to link to /account, a full screen you had to navigate
                  back out of — and with the products on separate hosts it was
                  also the only place that knew the other apps existed, which is
                  the wrong place for the way between them. The drawer is both:
                  who you are, and the switcher. See AccountDrawer. */}
              <button
                type="button"
                onClick={() => setAccountOpen(true)}
                className="hidden sm:inline text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {user?.username}
              </button>
            </div>
          </div>
        </div>

        {/* What this stage contains. Rendered under the stage row rather than as a
            dropdown so the whole stage is visible at once — the point of the
            restructure is that someone can SEE what "Produce" means without
            having to open it. */}
        {subNav && (
          <div className="hidden sm:block border-t border-gray-100 dark:border-gray-700/60">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-6 h-10">
                {subNav.items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={(e) => handleNavClick(item, e)}
                    className={`text-sm ${
                      activeItemIn(subNav, location.pathname) === item
                        ? 'text-gray-900 dark:text-gray-100 font-medium'
                        : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="sm:hidden border-t border-gray-200 dark:border-gray-700">
            {/* Grouped by stage, same as the desktop two-row nav. A flat list
                here would teach a different structure on a phone. */}
            <div className="py-2">
              {shownSections.map((section) => (
                <div key={section.key} className="py-1">
                  {section.items.length > 1 && (
                    <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                      {section.label}
                    </p>
                  )}
                  {section.items.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={(e) => {
                        handleNavClick(item, e);
                        setMobileMenuOpen(false);
                      }}
                      className={`flex items-center justify-between px-4 py-2 text-base font-medium ${
                        activeItemIn(section, location.pathname) === item
                          ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-500 dark:bg-blue-900/30 dark:text-blue-300'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100'
                      }`}
                    >
                      <span>
                        {section.items.length > 1 ? item.label : section.label}
                      </span>
                    </Link>
                  ))}
                </div>
              ))}
            </div>
            <div className="py-2 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={toggleTheme}
                className="w-full flex items-center px-4 py-2 text-base font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
              >
                <span className="w-5 h-5 mr-2 inline-flex items-center justify-center">
                  {theme === 'dark' ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                  )}
                </span>
                {theme === 'dark' ? 'Light theme' : 'Dark theme'}
              </button>
              <Link
                to="/settings"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center px-4 py-2 text-base font-medium ${
                  location.pathname === '/settings' || location.pathname.startsWith('/settings')
                    ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-500 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100'
                }`}
              >
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Settings
              </Link>
              {/* The username link is desktop-only, so mobile reaches the
                  account page (and Log out) from here. */}
              <Link
                to="/account"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center px-4 py-2 text-base font-medium ${
                  location.pathname === '/account'
                    ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-500 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100'
                }`}
              >
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {user?.username || 'Account'}
              </Link>
            </div>
          </div>
        )}
      </nav>

      {showInternals && (
        <div className="bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 px-4 py-1">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 text-xs">
            <span>
              <span className="font-semibold">Internals visible</span> - model
              chips, spend and agent diagnostics are showing. Customers do not
              see these.
            </span>
            <button
              onClick={() => setRevealed(false)}
              className="shrink-0 font-medium underline hover:no-underline"
            >
              Hide (ctrl+shift+alt)
            </button>
          </div>
        </div>
      )}

      {networkDisabledBanner && (
        <div className="bg-red-600 text-white px-4 py-2.5">
          <div className="max-w-7xl mx-auto flex items-start justify-between gap-3">
            <div className="flex items-start gap-2 min-w-0">
              <svg className="w-5 h-5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <span className="text-sm">{networkDisabledBanner}</span>
            </div>
            <button
              onClick={() => setNetworkDisabledBanner(null)}
              className="shrink-0 text-red-200 hover:text-white"
              aria-label="Dismiss"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Admin-only, because the fix is admin-only: both of these link to
          Connections → Google Ads, which has been an admin sub since keyword
          requests moved to the house connection. A non-admin got a loud banner
          telling them to fix something and a link to a tab they cannot open. */}
      {googleAdsExpiredBanner && isAdmin && (
        <div className="bg-amber-600 text-white px-4 py-2.5">
          <div className="max-w-7xl mx-auto flex items-start justify-between gap-3">
            <div className="flex items-start gap-2 min-w-0">
              <svg className="w-5 h-5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <span className="text-sm">
                Your Google Ads token has expired. Keyword volume data will not be updated until you reconnect.{' '}
                <Link to="/settings?tab=connections&sub=google-ads" className="underline font-medium hover:text-amber-100">
                  Reconnect Google Ads →
                </Link>
              </span>
            </div>
            <button
              onClick={() => setGoogleAdsExpiredBanner(false)}
              className="shrink-0 text-amber-200 hover:text-white"
              aria-label="Dismiss"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {googleAdsDemandDownBanner && isAdmin && (
        <div className="bg-amber-600 text-white px-4 py-2.5" data-testid="google-ads-demand-down">
          <div className="max-w-7xl mx-auto flex items-start justify-between gap-3">
            <div className="flex items-start gap-2 min-w-0">
              <svg className="w-5 h-5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <span className="text-sm">
                {googleAdsDemandDownBanner}{' '}
                <Link to="/settings?tab=connections&sub=google-ads" className="underline font-medium hover:text-amber-100">
                  Test Google Ads →
                </Link>
              </span>
            </div>
            <button
              onClick={() => setGoogleAdsDemandDownBanner(null)}
              className="shrink-0 text-amber-200 hover:text-white"
              aria-label="Dismiss"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {googleAdsNotConfiguredBanner && isAdmin && (
        <div className="bg-amber-500 text-white px-4 py-2.5">
          <div className="max-w-7xl mx-auto flex items-start justify-between gap-3">
            <div className="flex items-start gap-2 min-w-0">
              <svg className="w-5 h-5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm">
                {googleAdsNotConfiguredBanner}{' '}
                <Link to="/settings?tab=connections&sub=google-ads" className="underline font-medium hover:text-amber-100">
                  Connect Google Ads →
                </Link>
              </span>
            </div>
            <button
              onClick={() => setGoogleAdsNotConfiguredBanner(null)}
              className="shrink-0 text-amber-100 hover:text-white"
              aria-label="Dismiss"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Every page renders inside a rounded card floating on the page background — the
          same treatment as the boardroom. The full-height editors opt out.

          THEY OPT OUT OF THE WIDTH CAP TOO. `isFullHeightEditor` already existed and
          already matched these routes, but only ever removed the card — so the boardroom
          was still squeezed into 1280px while three columns (navigation, stage, minutes)
          need a floor of about 1030 before the stage stops being a board and starts being
          a list of ellipses. Everything else stays at 7xl: a settings page reads worse
          the wider it gets, and a research console reads better.

          The PADDING is deliberately unchanged. BoardroomRoom sizes itself against
          `calc(100vh - 7rem)`, and that 7rem encodes the nav height plus this element's
          `py` — change the padding here and the board desyncs there. */}
      <main
        className={`mx-auto py-4 sm:py-6 px-2 sm:px-6 lg:px-8 ${
          isFullHeightEditor ? '' : 'max-w-7xl'
        }`}
      >
        {/* A page that throws mid-render takes the whole React tree down with it unless
            something catches it here — and a white screen with no nav is unrecoverable
            when the data causing it is persisted. Keyed on the path so navigating away
            clears it. */}
        {/* Mounted once, above the page content, so it overlays whatever you
            were doing and dismisses back to it. */}
        {accountOpen && (
          <AccountDrawer
          shell={shell}
          onClose={() => setAccountOpen(false)}
          returnLabel={returnLabelFor(SECTIONS, location.pathname, SHELLS[shell].short)}
        />
        )}

        {/* SUNSETTING: you keep the product for now, and every page in its
            shell says so. The launcher card carries the same badge. */}
        {shellIsHeld && shellCatalog?.availability === 'SUNSETTING' && (
          <div
            className="mb-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-2 text-sm text-amber-900 dark:text-amber-100"
            data-testid="retiring-banner"
          >
            {SHELLS[shell].name} is being retired
            {shellCatalog.sunset_at
              ? ` on ${new Date(shellCatalog.sunset_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}`
              : ''}
            . What you make here stays on the artifacts shelf.
          </div>
        )}
        <PageErrorBoundary resetKey={location.pathname}>
          {isFullHeightEditor ? (
            <Outlet />
          ) : (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 shadow-sm p-4 sm:p-6 min-h-[calc(100vh-8rem)] overflow-hidden">
              <Outlet />
            </div>
          )}
        </PageErrorBoundary>
      </main>

      {/* Signed-in users otherwise have no route to the policies except the auth
          screens they've already passed and the Google Ads settings tab. */}
      {!isFullHeightEditor && (
        <footer className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 pb-6">
          <LegalLinks />
        </footer>
      )}
    </div>
  );
}
